import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Auth } from 'aws-amplify';
import cx from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { displayDuration } from 'utils';
import { otpCountdownTimeSelector } from 'store/selectors';
import { useCountdown, useStore } from 'hooks';
import { Input } from 'components';

import styles from './Authentication.module.scss';

export type ConfirmProps = {
  username: string;
  onSubmitted(): void;
  onCodeResent(): void;
};

type FormValues = {
  code: string;
};

const validationSchema = yup.object().shape({
  code: yup.string().required('Code is required')
});

const Confirm = ({ username, onSubmitted, onCodeResent }: ConfirmProps) => {
  const otpCountdownTime = useStore(otpCountdownTimeSelector);
  const countdown = useCountdown(otpCountdownTime);
  const form = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onChange',
    defaultValues: {
      code: ''
    }
  });

  const [isResending, setResending] = useState(false);

  const handleOnResendCode = async () => {
    try {
      setResending(true);
      await Auth.resendSignUp(username);
      onCodeResent();
    } catch (e) {
      if (e instanceof Object && 'message' in e) {
        const parsedError = e as { message: string };

        form.setError('code', { message: parsedError.message, type: 'server' });
      }
    } finally {
      setResending(false);
    }
  };

  const handleOnSubmit = async (validValues: FormValues) => {
    const { code } = validValues;

    try {
      await Auth.confirmSignUp(username, code);
      onSubmitted();
    } catch (e) {
      if (e instanceof Object && 'message' in e) {
        const parsedError = e as { message: string };

        form.setError('code', { message: parsedError.message, type: 'server' });
      }
    }
  };

  return (
    <div className={styles.confirm}>
      <form onSubmit={form.handleSubmit(handleOnSubmit)}>
        <div className={styles.fields}>
          <Controller
            control={form.control}
            name="code"
            render={({ field, fieldState }) => (
              <Input
                className={styles.code}
                invalid={!!fieldState.error?.message}
                feedback={fieldState.error?.message}
                placeholder="Enter verification code"
                after={
                  countdown.duration > 0 ? (
                    <span className={styles.count}>{displayDuration(countdown.duration)}</span>
                  ) : (
                    <button
                      type="button"
                      className={styles.resend}
                      disabled={!!countdown.duration || isResending}
                      onClick={handleOnResendCode}
                    >
                      Resend code
                    </button>
                  )
                }
                {...field}
              />
            )}
          />
        </div>

        <button
          type="submit"
          disabled={form.formState.isSubmitting}
          className={cx({ [styles.active]: form.formState.isSubmitted })}
        >
          Confirm
        </button>
      </form>
    </div>
  );
};

export default Confirm;
